/**
 * DryRunPublish Component
 * ========================
 * Phase 8A: Test publish content without actually posting
 * Validates payload and logs the attempt
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  Play, CheckCircle, XCircle, AlertTriangle, Loader2, Eye, FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DryRunPublish = ({
  open,
  onClose,
  entityType = 'design',
  entityId,
  entityName,
  approvalStatus
}) => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const getHeaders = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch connected accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await axios.get(`${API}/social/accounts`, { headers: getHeaders() });
        setAccounts(response.data.accounts || []);
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      }
    };

    if (open) {
      fetchAccounts();
      setResult(null);
    }
  }, [open]);

  // Perform dry run
  const executeDryRun = async () => {
    if (!selectedAccount) {
      toast.error('Please select an account');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(
        `${API}/social/accounts/${selectedAccount}/dry-run`,
        {
          entity_type: entityType,
          entity_id: entityId,
          caption: caption || null,
          hashtags: hashtags ? hashtags.split(',').map(t => t.trim()) : null
        },
        { headers: getHeaders() }
      );

      setResult(response.data);

      if (response.data.success) {
        toast.success('Dry run successful! Payload is valid.');
      } else {
        toast.error('Dry run found validation errors');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Dry run failed';
      toast.error(detail);
      setResult({
        success: false,
        validation_errors: [detail],
        warnings: []
      });
    } finally {
      setLoading(false);
    }
  };

  const connectedAccounts = accounts.filter(a => a.status === 'connected');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5 text-blue-500" />
            Dry Run Publish
          </DialogTitle>
          <DialogDescription>
            Test your publish without actually posting. Validates content format and platform requirements.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Entity Info */}
          <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                <span className="font-medium dark:text-white">{entityName}</span>
              </div>
              <Badge variant={approvalStatus === 'approved' ? 'default' : 'secondary'}>
                {approvalStatus || 'not_submitted'}
              </Badge>
            </div>
          </div>

          {approvalStatus !== 'approved' && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm text-yellow-800 dark:text-yellow-300">
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              Content must be approved before it can be published
            </div>
          )}

          {/* Account Selection */}
          <div>
            <Label>Select Account</Label>
            {connectedAccounts.length === 0 ? (
              <p className="text-sm text-muted-foreground mt-1">
                No connected accounts. Connect an account in Social Settings first.
              </p>
            ) : (
              <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select an account" />
                </SelectTrigger>
                <SelectContent>
                  {connectedAccounts.map(account => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.account_name} ({account.platform})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Caption Override */}
          <div>
            <Label>Caption (Optional Override)</Label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Leave empty to use design description"
              className="mt-1 min-h-[80px]"
            />
          </div>

          {/* Hashtags */}
          <div>
            <Label>Hashtags (comma separated)</Label>
            <Textarea
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
              placeholder="#marketing, #design, #social"
              className="mt-1 min-h-[60px]"
            />
          </div>

          {/* Result */}
          {result && (
            <Card className={result.success ? 'border-green-500' : 'border-red-500'}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  {result.success ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-green-700 dark:text-green-400">Validation Passed</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 text-red-500" />
                      <span className="text-red-700 dark:text-red-400">Validation Failed</span>
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-3">
                {/* Errors */}
                {result.validation_errors?.length > 0 && (
                  <div>
                    <p className="font-medium text-red-700 dark:text-red-400 mb-1">Errors:</p>
                    <ul className="list-disc list-inside text-red-600 dark:text-red-400 space-y-1">
                      {result.validation_errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Warnings */}
                {result.warnings?.length > 0 && (
                  <div>
                    <p className="font-medium text-yellow-700 dark:text-yellow-400 mb-1">Warnings:</p>
                    <ul className="list-disc list-inside text-yellow-600 dark:text-yellow-400 space-y-1">
                      {result.warnings.map((warn, i) => (
                        <li key={i}>{warn}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Payload Preview */}
                {result.payload && (
                  <div>
                    <p className="font-medium text-slate-700 dark:text-slate-300 mb-1">Payload Preview:</p>
                    <pre className="p-2 bg-slate-100 dark:bg-slate-800 rounded text-xs overflow-x-auto">
                      {JSON.stringify(result.payload, null, 2)}
                    </pre>
                  </div>
                )}

                {result.log_id && (
                  <p className="text-xs text-muted-foreground">
                    Log ID: {result.log_id}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={executeDryRun}
            disabled={loading || !selectedAccount || approvalStatus !== 'approved'}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            Run Dry Test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DryRunPublish;
